import React, { useState, useEffect, useCallback, useRef } from 'react';
import { employeeClockAPI } from '../lib/api';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Clock, LogIn, LogOut, Coffee, Sun, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CLOCK_OUT_REASONS = [
  { value: 'end_of_day', label: 'End of Day', icon: Sun },
  { value: 'lunch_break', label: 'Lunch Break', icon: Coffee },
];

function formatElapsed(clockInIso) {
  if (!clockInIso) return '';
  const ms = Date.now() - new Date(clockInIso).getTime();
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function EmployeeClockControl() {
  const [clockedIn, setClockedIn] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [elapsed, setElapsed] = useState('');
  const timerRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await employeeClockAPI.status();
      setClockedIn(res.data.clocked_in);
      setActiveSession(res.data.active_session);
    } catch { /* non-critical */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Elapsed timer when clocked in
  useEffect(() => {
    if (clockedIn && activeSession?.clock_in) {
      setElapsed(formatElapsed(activeSession.clock_in));
      timerRef.current = setInterval(() => {
        setElapsed(formatElapsed(activeSession.clock_in));
      }, 30000); // update every 30s
      return () => clearInterval(timerRef.current);
    } else {
      setElapsed('');
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [clockedIn, activeSession?.clock_in]);

  const handleClockIn = async () => {
    setActing(true);
    try {
      const res = await employeeClockAPI.clockIn();
      setClockedIn(true);
      setActiveSession(res.data.session);
      toast.success('Clocked in');
      setPopoverOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to clock in');
    } finally { setActing(false); }
  };

  const handleClockOut = async (reason) => {
    setActing(true);
    try {
      const res = await employeeClockAPI.clockOut({ reason });
      setClockedIn(false);
      setActiveSession(null);
      const label = CLOCK_OUT_REASONS.find(r => r.value === reason)?.label || reason;
      toast.success(`Clocked out — ${label} (${res.data.session.duration_minutes}min)`);
      setPopoverOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to clock out');
    } finally { setActing(false); }
  };

  if (loading) return null;

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={
            // Top-nav usage: force white contrast against the colored navbar
            // in both light and dark themes. Use a subtle border to preserve
            // the "outline" look without painting a solid bg that washes out
            // against the dark gradient.
            `h-8 gap-1.5 text-xs font-medium ${
              clockedIn
                ? 'bg-green-600 hover:bg-green-700 text-white hover:text-white'
                : 'border border-white/30 text-white hover:bg-white/10 hover:text-white focus-visible:text-white'
            }`
          }
          data-testid="employee-clock-trigger"
        >
          <Clock className="w-3.5 h-3.5" />
          {clockedIn ? (
            <span className="hidden sm:inline">{elapsed || 'On Clock'}</span>
          ) : (
            <span className="hidden sm:inline">Clock In</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="end" side="bottom" sideOffset={8}>
        <div className="p-3 space-y-2" data-testid="employee-clock-popover">
          {/* Status header */}
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Time Clock</p>
            {clockedIn && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-[10px] font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                {elapsed}
              </span>
            )}
          </div>

          {!clockedIn ? (
            /* Clock In */
            <Button
              className="w-full gap-2 bg-green-600 hover:bg-green-700"
              onClick={handleClockIn}
              disabled={acting}
              data-testid="clock-in-btn"
            >
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              Clock In
            </Button>
          ) : (
            /* Clock Out reasons */
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">Clock Out</p>
              {CLOCK_OUT_REASONS.map(r => {
                const Icon = r.icon;
                return (
                  <button
                    key={r.value}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm hover:bg-accent transition-colors text-left"
                    onClick={() => handleClockOut(r.value)}
                    disabled={acting}
                    data-testid={`clock-out-${r.value}`}
                  >
                    {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4 text-muted-foreground" />}
                    <span>{r.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
